// @ts-strict-ignore

import { FC } from 'react';

import { Box, css, styled } from '@mui/material';
import { trackDropdownInputUsageAnalyticsEvent } from 'analytics/events/input-usage';
import moment from 'moment';
import { useFormContext, useWatch } from 'react-hook-form';

import { ActionMeta } from 'react-select';

import { useStores } from 'mobx/hooks/useStores';

import {
  getTodayFormattedDate,
  HOUR_IN_MILLISECONDS,
  LONG_DATE_FORMAT,
  WEEK_IN_MILLISECONDS
} from 'utils/DateUtils';

import {
  DrugSpecificProtocol,
  DrugSpecificReportProtocolTemplate,
  DrugType
} from 'models/DrugSpecific';
import ScheduledProtocol from 'models/ScheduledProtocol';
import { SelectOption } from 'models/SelectOption';

import { DrugSpecificProtocolForm } from 'views/Modals/ScheduledProtocolModal';
import { CustomDrugSpecificFields } from 'views/Widgets/CustomDrugSpecificFields';
import { defaultEndDateDurationUnitOptions } from 'views/Widgets/EndProtocolOptions';

import { RecurringDrugSpecificFields } from 'views/Widgets/RecurringDrugSpecificFields';
import ToggleBar from 'views/Widgets/ToggleBar';

import { FormAutocomplete } from 'components/UIkit/atoms/Dropdown';
import { Text } from 'components/UIkit/atoms/Text';

import {
  defaultDrugSpecificIntervalOptions,
  drugSpecificEndOptions,
  specificDrugTypeToLabelMap
} from './DrugSpecificProtocolFields.constants';

import './OralProtocolFields.scss';

interface Props {
  protocol?: ScheduledProtocol;
}

enum TabOption {
  Custom = 'custom',
  Recurring = 'recurring'
}

export const DrugSpecificProtocolFields: FC<Props> = ({ protocol }) => {
  const { control, setValue, reset } = useFormContext<DrugSpecificProtocolForm>();
  const { constantsStore } = useStores();
  const [drug, type] = useWatch({
    control,
    name: ['drug', 'type']
  });
  const currentDrugCustomWithoutTypeId = constantsStore.getSpecificDrugTypeId(
    drug?.value,
    DrugSpecificProtocol.SpecificTimesHourlyWithoutHospitalization
  );

  const currentDrugCustomWithTypeId = constantsStore.getSpecificDrugTypeId(
    drug?.value,
    DrugSpecificProtocol.SpecificTimesHourlyWithHospitalization
  );

  const isCustom =
    type &&
    (type.value === currentDrugCustomWithoutTypeId || type.value === currentDrugCustomWithTypeId);

  const hasDrugValue = Boolean(drug?.value);

  const setDrugDefaults = (drugTemplate: DrugSpecificReportProtocolTemplate) => {
    let defaultDrugTimeUnit = WEEK_IN_MILLISECONDS;
    let defaultDrugMultiplier =
      drugTemplate.timeBetweenReportsInMilliseconds / WEEK_IN_MILLISECONDS;
    const defaultEndOption = drugSpecificEndOptions.find(
      (option: SelectOption<number>) => option.value === drugTemplate.endType
    );

    // Determine default start date based on different conditions
    let defaultStartDate;

    // Priority 1: If delayInHours is specified, use it regardless of protocol type
    if (drugTemplate.delayInHours) {
      const delayedDate = new Date(Date.now() + drugTemplate.delayInHours * HOUR_IN_MILLISECONDS);
      // Use moment object for DateTimePicker (hourly/custom) or formatted string for date input
      if (drugTemplate.type === DrugSpecificProtocol.Hourly || drugTemplate.reportTimes) {
        defaultStartDate = moment(delayedDate);
      } else {
        defaultStartDate = moment(delayedDate).format(LONG_DATE_FORMAT);
      }
    }
    // Priority 2: Handle protocol-specific defaults only if no delayInHours
    else if (drugTemplate.type === DrugSpecificProtocol.Hourly) {
      defaultStartDate = moment(new Date());
    }
    // Priority 3: For non-hourly protocols, use formatted date string for date input
    else {
      defaultStartDate = getTodayFormattedDate(LONG_DATE_FORMAT);
    }

    // Handle frequency settings for hourly protocols
    if (drugTemplate.type === DrugSpecificProtocol.Hourly) {
      if (drugTemplate.reportTimes) {
        // For custom times, don't set frequency fields as they're not used
        defaultDrugTimeUnit = HOUR_IN_MILLISECONDS;
        defaultDrugMultiplier = 1;
      } else {
        defaultDrugTimeUnit = HOUR_IN_MILLISECONDS;
        defaultDrugMultiplier =
          drugTemplate.timeBetweenReportsInMilliseconds / HOUR_IN_MILLISECONDS;
      }
    }
    setValue('selectedStartDate', defaultStartDate);
    setValue('reportTimes', drugTemplate.reportTimes);
    setValue('afterHoursProvider', null);
    setValue('frequencyMultiplier', defaultDrugMultiplier);
    setValue(
      'selectedFrequency',
      defaultDrugSpecificIntervalOptions.find((option) => option.value === defaultDrugTimeUnit)
    );
    setValue('selectedEndOption', defaultEndOption);
    setValue('endDateDuration', drugTemplate.duration);
    setValue(
      'endDateDurationUnit',
      defaultEndDateDurationUnitOptions.find(
        (option) => option.value === drugTemplate.durationUnitType
      )
    );
  };

  const onDrugChange = (
    option: SelectOption<number>,
    actionMeta: ActionMeta<SelectOption<number>>,
    eventKey: string
  ) => {
    trackDropdownInputUsageAnalyticsEvent(actionMeta, 'Drug', eventKey === 'Enter', hasDrugValue, {
      virtual_page: 'report protocol modal'
    });

    if (option.value === protocol?.info?.drugId) {
      reset();
      return;
    }
    const drug = constantsStore.getSpecificDrugById(option.value);
    const defaultDrugTemplate = drug.reportProtocolTemplates[0];
    let label = specificDrugTypeToLabelMap[defaultDrugTemplate.type];
    if (defaultDrugTemplate.reportTimes) {
      label =
        specificDrugTypeToLabelMap[
          defaultDrugTemplate.withHospitalization
            ? DrugSpecificProtocol.SpecificTimesHourlyWithHospitalization
            : DrugSpecificProtocol.SpecificTimesHourlyWithoutHospitalization
        ];
    }
    if (
      drug.drugType === DrugType.Bispecific &&
      defaultDrugTemplate.type === DrugSpecificProtocol.WeeklyInfection
    ) {
      label = specificDrugTypeToLabelMap[DrugSpecificProtocol.WeeklyInfectionBiSpecific];
    }
    setValue('type', {
      label,
      value: defaultDrugTemplate.id
    });

    setDrugDefaults(defaultDrugTemplate);
  };
  const getTabOptions = () => {
    return [
      {
        label: 'Request Report at Recurring Intervals',
        value: TabOption.Recurring,
        disabled: isCustom
      },
      { label: 'Request Report at Custom Times', value: TabOption.Custom, disabled: !isCustom }
    ];
  };
  const onTypeChange = (
    option: SelectOption<number>,
    actionMeta: ActionMeta<SelectOption<number>>,
    eventKey: string
  ) => {
    trackDropdownInputUsageAnalyticsEvent(
      actionMeta,
      'Type',
      eventKey === 'Enter',
      Boolean(type?.value),
      {
        virtual_page: 'report protocol modal'
      }
    );

    if (option.value === protocol?.reportProtocolTemplateId) {
      reset();
      return;
    }
    const drugTemplate = constantsStore.getSpecificDrugTemplate(drug?.value, option.value);

    setDrugDefaults(drugTemplate);
  };

  return (
    <>
      <Box display="flex" width="100%" gap={20}>
        <StyledSection>
          <FormAutocomplete
            label="Drug"
            isRequired
            options={constantsStore.specificDrugsForSelect}
            name="drug"
            onChange={onDrugChange}
          />
        </StyledSection>

        {hasDrugValue && (
          <StyledSection>
            <FormAutocomplete
              label="Protocol"
              isRequired
              options={constantsStore.getSpecificDrugTypesForSelect(drug.value)}
              name="type"
              onChange={onTypeChange}
            />
          </StyledSection>
        )}
      </Box>

      {hasDrugValue && (
        <>
          <Text
            variant="body2"
            color="secondary"
            display="inline-block"
            textTransform="uppercase"
            mt={20}
          >
            Report Frequency
          </Text>

          <StyledToggleContainer>
            <ToggleBar
              id="recurring-times-toggle"
              options={getTabOptions()}
              selected={isCustom ? TabOption.Custom : TabOption.Recurring}
              onOptionSelected={() => {}}
              isSquared
              size="small"
            />
          </StyledToggleContainer>
        </>
      )}
      {hasDrugValue && (!isCustom ? <RecurringDrugSpecificFields /> : <CustomDrugSpecificFields />)}
    </>
  );
};

const StyledToggleContainer = styled(Box)(
  ({ theme }) => css`
    margin-top: ${theme.spacing(20)};
    display: flex;
  `
);

const StyledSection = styled(Box)(
  ({ theme }) => css`
    margin: ${theme.spacing(20, 0)};
    display: flex;
    align-items: center;
    gap: ${theme.spacing(20)};
    width: 100%;

    .styled-select {
      width: 100%;
    }

    .styled-input {
      width: 100%;
    }
  }
  `
);
